import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type Token,
  type ActionPerformed,
  type PushNotificationSchema,
} from '@capacitor/push-notifications';
import { toast } from 'sonner';
import { httpClient } from './HttpClient.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationNavigateCallback = (path: string) => void;

/** Matches the data payload sent by the backend NotificationService */
interface PolicyNotificationData {
  type: 'policy';
  policyId: string;
  clientName: string;
  policyType: string;
  endDate: string;
  daysLeft: string;
  remindedAt: string;
}

interface EnquiryNotificationData {
  type: 'enquiry';
  enquiryId: string;
  name: string;
  policyType: string;
  remindOn: string;
  remindedAt: string;
}

type NotificationData = PolicyNotificationData | EnquiryNotificationData;

// ─── Token helpers ────────────────────────────────────────────────────────────

/**
 * Sends the FCM token to the backend so the server can store it and push
 * renewal reminders to this specific device.
 */
async function sendTokenToBackend(fcmToken: string): Promise<void> {
  try {
    await httpClient.patch('/api/v1/notifications/token', { fcmToken });
    console.info('[FCM] Token registered with backend.');
  } catch (err) {
    console.error('[FCM] Failed to register token with backend:', err);
  }
}

/**
 * Retrieves the current FCM token for this device.
 * Returns null on web or if permissions were denied.
 */
export async function getFcmToken(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;

  try {
    const result = await PushNotifications.checkPermissions();
    if (result.receive !== 'granted') {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== 'granted') {
        console.warn('[FCM] Notification permission denied.');
        return null;
      }
    }

    return await new Promise<string | null>((resolve) => {
      // Register — the token arrives via the 'registration' event
      void PushNotifications.register();

      const timeout = setTimeout(() => {
        console.warn('[FCM] Token retrieval timed out.');
        resolve(null);
      }, 8000);

      void PushNotifications.addListener('registration', (token: Token) => {
        clearTimeout(timeout);
        console.info('[FCM] Device token obtained:', token.value);
        resolve(token.value);
      });

      void PushNotifications.addListener('registrationError', (err) => {
        clearTimeout(timeout);
        console.error('[FCM] Registration error:', err);
        resolve(null);
      });
    });
  } catch (err) {
    console.error('[FCM] Error getting FCM token:', err);
    return null;
  }
}

// ─── Notification data parser ─────────────────────────────────────────────────

function parseNotificationData(
  notification: PushNotificationSchema,
): NotificationData | null {
  const raw = notification.data as Record<string, unknown> | undefined;
  if (!raw || typeof raw.type !== 'string') return null;

  if (raw.type === 'policy') {
    return {
      type: 'policy',
      policyId: String(raw.policyId ?? ''),
      clientName: String(raw.clientName ?? ''),
      policyType: String(raw.policyType ?? ''),
      endDate: String(raw.endDate ?? ''),
      daysLeft: String(raw.daysLeft ?? ''),
      remindedAt: String(raw.remindedAt ?? ''),
    };
  }

  if (raw.type === 'enquiry') {
    return {
      type: 'enquiry',
      enquiryId: String(raw.enquiryId ?? ''),
      name: String(raw.name ?? ''),
      policyType: String(raw.policyType ?? ''),
      remindOn: String(raw.remindOn ?? ''),
      remindedAt: String(raw.remindedAt ?? ''),
    };
  }

  return null;
}

function buildNavigationPath(data: NotificationData): string {
  if (data.type === 'policy') return `/policies/${data.policyId}`;
  if (data.type === 'enquiry') return `/enquiries/${data.enquiryId}`;
  return '/policies';
}

// ─── Main init ────────────────────────────────────────────────────────────────

let listenersAttached = false;

/**
 * Initialises the push notification system.
 *
 * - Requests permission
 * - Registers the device with FCM
 * - Sends the token to the backend (token refresh handled automatically)
 * - Attaches listeners for foreground notifications and tap actions
 *
 * Pass a `navigate` callback (e.g. from react-router `useHistory`) so tapping
 * a notification opens the correct policy / enquiry detail page.
 *
 * Safe to call multiple times — listeners are only registered once.
 */
export async function initPushNotifications(
  navigate: NotificationNavigateCallback,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // ── Permission & registration ─────────────────────────────────────────────
  const permStatus = await PushNotifications.checkPermissions();
  if (permStatus.receive !== 'granted') {
    const result = await PushNotifications.requestPermissions();
    if (result.receive !== 'granted') {
      console.warn('[FCM] Push notification permission denied.');
      return;
    }
  }

  await PushNotifications.register();

  if (listenersAttached) return;
  listenersAttached = true;

  // ── Token registration (initial + refresh) ────────────────────────────────
  void PushNotifications.addListener('registration', (token: Token) => {
    console.info('[FCM] Token registered/refreshed:', token.value);
    void sendTokenToBackend(token.value);
  });

  void PushNotifications.addListener('registrationError', (err) => {
    console.error('[FCM] Registration error:', err);
  });

  // ── Foreground notification received ─────────────────────────────────────
  void PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      const data = parseNotificationData(notification);
      const title = notification.title ?? 'Reminder';
      const body = notification.body ?? '';

      // Show as an in-app toast with a tap-to-navigate action
      if (data) {
        const path = buildNavigationPath(data);
        toast(title, {
          description: body,
          duration: 8000,
          action: {
            label: data.type === 'policy' ? 'View Policy' : 'View Enquiry',
            onClick: () => {
              navigate(path);
            },
          },
        });
      } else {
        toast(title, { description: body, duration: 6000 });
      }
    },
  );

  // ── Notification tapped (background / killed state) ───────────────────────
  void PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      const data = parseNotificationData(action.notification);
      if (data) {
        const path = buildNavigationPath(data);
        console.info('[FCM] Notification tapped → navigating to', path);
        navigate(path);
      }
    },
  );
}
